import {Injectable} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {CoreConfigService} from "../../../service/core-config.service";
import {CoreMetaExternalSideCarDto} from "../dto/meta-external-side-car.dto";
import {CoreMetaExternalSideCarScopeDto} from "../dto/meta-external-side-car-scope.dto";
import {CoreMetaExternalSideCarFieldDto} from "../dto/meta-external-side-car-field.dto";

/**
 * Bounded-external side-car DISCOVERY — the three GETs of `api-contract.md`
 * §"Endpoints (core/meta) — Seitenwagen-Discovery" (owner alpha-extensibility-specialist).
 *
 * There is no meta-model way to ask "which side-cars does entity X have" — `MetaService.getByAlias`
 * never lists a side-car join (it is not a `MetaDataJoin`, see `requirement.md` §X). These three
 * endpoints are the only source: they walk the `BoundedExternalSideCarInstance` marker interface
 * generically (never a hardcoded `ci`/`pm`/`xx` import), so a NEW side-car needs no builder change.
 *
 * Placed next to {@link MetaService} (same `core/meta` API surface), but kept as its own
 * `@Injectable` rather than added to `MetaService` — discovery has a completely different
 * cardinality/cache shape (per-host, per-instance, per-scope) than entity metadata.
 */
@Injectable({providedIn: 'root'})
export class CoreMetaExternalSideCarService {

    public apiUrl: string;

    constructor(protected http: HttpClient, private configService: CoreConfigService) {
        this.apiUrl = configService.getConfig().API_URL + "/core/meta";
    }

    /**
     * All side-cars available on a host entity that the current user may read.
     * @param hostAlias object-type alias of the host entity, e.g. `xx.XxPerson`.
     */
    public getSideCarsForHost(hostAlias: string): Observable<CoreMetaExternalSideCarDto[]> {

        const params = new HttpParams().set('hostAlias', hostAlias);
        return this.http.get<CoreMetaExternalSideCarDto[]>(
            `${this.apiUrl}/externalSideCars`,
            {params}
        );
    }

    /**
     * The selectable scopes (versions) of one side-car instance.
     * @param instanceAlias `CoreMetaExternalSideCarDto.instanceAlias` of the chosen side-car.
     */
    public getScopes(instanceAlias: string): Observable<CoreMetaExternalSideCarScopeDto[]> {

        return this.http.get<CoreMetaExternalSideCarScopeDto[]>(
            `${this.apiUrl}/externalSideCars/${instanceAlias}/scopes`
        );
    }

    /**
     * The TypeFields of one side-car instance, scoped to one version.
     * @param instanceAlias `CoreMetaExternalSideCarDto.instanceAlias` of the chosen side-car.
     * @param scopeId       `CoreMetaExternalSideCarScopeDto.id` of the chosen scope/version.
     */
    public getFields(instanceAlias: string, scopeId: number): Observable<CoreMetaExternalSideCarFieldDto[]> {

        const params = new HttpParams().set('scopeId', scopeId);
        return this.http.get<CoreMetaExternalSideCarFieldDto[]>(
            `${this.apiUrl}/externalSideCars/${instanceAlias}/fields`,
            {params}
        );
    }

}
